import { useState, useEffect } from 'react';

export const useLogin = () => {
  // Initialisiere user mit Daten aus sessionStorage falls vorhanden
  const [user, setUser] = useState(() => {
    try {
      const savedUser = sessionStorage.getItem('syllabus_user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      console.error('Fehler beim Laden der Benutzerdaten aus sessionStorage:', error);
      return null;
    }
  });
  
  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');

  // Speichere user-Änderungen in sessionStorage
  useEffect(() => {
    if (user) {
      try {
        sessionStorage.setItem('syllabus_user', JSON.stringify(user));
      } catch (error) {
        console.error('Fehler beim Speichern der Benutzerdaten in sessionStorage:', error);
      }
    } else {
      sessionStorage.removeItem('syllabus_user');
    }
  }, [user]);

  const handleLoginOpen = () => {
    setLoginError('');
    setShowLogin(true);
  };
  const handleLoginClose = () => {
    setLoginError('');
    setShowLogin(false);
  };

  const handleInputChange = (e) => {
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      // Importiere die API-Funktion dynamisch
      const { checkLogin } = await import('../api');
      const data = await checkLogin(loginForm.username, loginForm.password);
      
      const newUser = { 
        name: data.name, 
        username: data.username, 
        email: data.email, 
        canEdit: data.canEdit || false,
        isLearner: data.isLearner || false 
      };
      
      setUser(newUser);
      setShowLogin(false);
      setLoginForm({ username: '', password: '' });
      setLoginError('');
      
      // Wenn es sich um einen Lernenden handelt, versuche automatisch den ersten Plan zu laden
      if (newUser.isLearner) {
        try {
          // Kurz warten, damit die User-Daten gesetzt sind
          setTimeout(async () => {
            const { fetchStudentPlans } = await import('../api');
            const result = await fetchStudentPlans(newUser.username);
            
            if (result.success && result.plans && result.plans.length > 0) {
              // Dispatche ein Event, damit die ResizableLayout-Komponente reagieren kann
              window.dispatchEvent(new CustomEvent('studentLoggedIn', {
                detail: {
                  user: newUser,
                  plans: result.plans,
                  shouldAutoLoadFirstPlan: true
                }
              }));
            }
          }, 500);
        } catch (error) {
          console.error('Fehler beim automatischen Laden der Lernenden-Pläne:', error);
          // Fehler nicht anzeigen, da Login erfolgreich war
        }
      }
    } catch (error) {
      setLoginError(error.message || 'Login fehlgeschlagen');
    }
  };

  const handleLogout = async () => {
    try {
      // Importiere die API-Funktion dynamisch
      const { logout } = await import('../api');
      await logout();
    } catch (error) {
      console.error('Fehler beim Logout:', error);
    } finally {
      // Dispatche Event, um alle geladenen Plan-Daten zu löschen
      window.dispatchEvent(new CustomEvent('userLogout'));
      
      // Lösche den Benutzer aus dem State (dies löst auch das Löschen aus sessionStorage aus)
      setUser(null);
    }
  };

  return {
    user,
    showLogin,
    loginForm,
    loginError,
    handleLoginOpen,
    handleLoginClose,
    handleInputChange,
    handleLoginSubmit,
    handleLogout
  };
};
